import { ToggleOnOutlined as ToggleOnOutlinedIcon } from 'components/icons';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Checkbox } from 'components/ui/checkbox';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useCallback, useMemo } from 'react';
import { useLazyGetFeatureFlagsWithFiltersQuery, useSetFeatureFlagEnabledMutation } from 'store/api/feature-flags.ts';
import { FeatureFlagListElement } from 'types/feature-flags.ts';
import { errorAlert, successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';

const FeatureFlagsTable = () => {
    const isNew = useIsNewDesign();
    const [fetchData, { data, isFetching }] = useLazyGetFeatureFlagsWithFiltersQuery();
    const canEditFeatureFlags = useHasPermissions(['UPDATE.FEATURE_FLAGS']);
    const [setFeatureFlagEnabled, { isLoading: isUpdating }] = useSetFeatureFlagEnabledMutation();

    const handleToggle = useCallback(
        async (featureCode: string, enabled: boolean) => {
            try {
                await setFeatureFlagEnabled({ featureCode, enabled }).unwrap();
                successAlert(t('feature-flag.update-success'));
            } catch {
                errorAlert(t('feature-flag.update-error'));
            }
        },
        [setFeatureFlagEnabled]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'code' }, { selector: 'texts.value' }],
        }),
        []
    );

    const columns = useMemo<DataTableColumnDef<FeatureFlagListElement>[]>(
        () => [
            {
                header: t('feature-flag.is-enabled'),
                accessorKey: 'enabled',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 80,
                },
                cell: ({ row }) => (
                    <Checkbox
                        variant="secondary"
                        aria-label={row.original.featureCode}
                        checked={!!row.original.enabled}
                        disabled={!canEditFeatureFlags || isUpdating}
                        onCheckedChange={(checked) => handleToggle(row.original.featureCode, checked)}
                    />
                ),
            },
            {
                header: t('feature-flag.feature-code'),
                accessorKey: 'featureCode',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                    // Feature codes are long single-line SCREAMING_SNAKE strings, and a weighted column
                    // cannot grow to fit them, so floor it near that content width to keep codes
                    // readable (description absorbs the squeeze) instead of clipping.
                    minPx: 560,
                },
                cell: ({ row }) => {
                    const { featureCode } = row.original;
                    return <div className="flex flex-col">{featureCode ?? '-'}</div>;
                },
            },
            {
                header: t('feature-flag.description'),
                accessorKey: 'description',
                enableSorting: false,
                meta: {
                    width: '50%',
                    widthWeight: 3.5,
                    minPx: 320,
                },
                cell: ({ row }) => {
                    const { description } = row.original;
                    return <div className="flex flex-col">{description ?? '-'}</div>;
                },
            },
        ],
        [canEditFeatureFlags, isUpdating, handleToggle]
    );

    const table = (
        <ListTableWithFilters<FeatureFlagListElement>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false). Lives in the Settings tabs layout, which grows to
            // content and never hands the table a bounded scroller — so cap the container height
            // here to make it the virtualizer's scroll element and let row windowing engage.
            columnConfigId="feature-flags"
            dataTableClassName="max-h-[calc(100vh-20rem)]"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="feature-flag.plural"
            singularTextId="feature-flag.singular"
            noEntriesId="feature-flag.no-feature-flag-found"
            noEntriesBodyId="feature-flag.empty.body"
            noEntriesIcon={ToggleOnOutlinedIcon}
            rowsPerPage={300}
            fetchData={fetchData}
            filterConfig={filterConfig}
        />
    );

    // The new design's Admin page gives the table its whole width, as it does the other Admin lists.
    if (isNew) {
        return table;
    }
    return (
        // Classic centres the table. Flex column (not a plain block) so the v8 DataTable stretches to
        // this bounded width at mount and the managed layout resolves columns across it; a block
        // wrapper let the table shrink to its intrinsic width and under-fill.
        <div className="mx-auto flex w-4/5 max-w-[1300px] flex-col">{table}</div>
    );
};

export default FeatureFlagsTable;
